import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TimezoneStore {
  private readonly STORAGE_KEY = 'user_timezone';
  private readonly timeZoneSubject: BehaviorSubject<string>;

  constructor() {
    const stored = this.getStoredTimeZone();
    const initial = stored || this.detectBrowserTimeZone();

    this.timeZoneSubject = new BehaviorSubject<string>(initial);
    this.saveToStorage(initial);
  }

  get currentTimeZone(): string {
    return this.timeZoneSubject.value;
  }

  setTimeZone(timeZone: string): void {
    if (!timeZone) {
      return;
    }

    this.timeZoneSubject.next(timeZone);
    this.saveToStorage(timeZone);
  }

  formatForDisplay(
    utcDateString: string | null | undefined,
    format: 'short' | 'medium' | 'long' | 'full' = 'medium'
  ): string {
    if (!utcDateString) {
      return '';
    }

    const timestamp = new Date(utcDateString);
    if (Number.isNaN(timestamp.getTime())) {
      return '';
    }

    const options = this.getFormatOptions(format);

    return new Intl.DateTimeFormat('en-US', {
      ...options,
      timeZone: this.currentTimeZone
    }).format(timestamp);
  }

  private detectBrowserTimeZone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  }

  private getStoredTimeZone(): string | null {
    try {
      return localStorage.getItem(this.STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private saveToStorage(timeZone: string): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, timeZone);
    } catch {
      return;
    }
  }

  private getFormatOptions(format: 'short' | 'medium' | 'long' | 'full'): Intl.DateTimeFormatOptions {
    switch (format) {
      case 'short':
        return {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        };
      case 'medium':
        return {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        };
      case 'long':
        return {
          year: 'numeric',
          month: 'long',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          timeZoneName: 'short'
        };
      case 'full':
        return {
          year: 'numeric',
          month: 'long',
          day: '2-digit',
          weekday: 'long',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          timeZoneName: 'long'
        };
      default:
        return {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        };
    }
  }
}
